/* =============================================================
   countryData.ts — United Dream Mongolia
   Single source of truth for all 5 country detail pages.
   ============================================================= */

export interface VisaType {
  name: string;
  duration: string;
  price: string;
  description: string;
  processingTime: string;
}

export interface CountryData {
  slug: string;
  flag: string;
  name: string;
  nameEn: string;
  tagline: string;
  heroColor: string;
  accentColor: string;
  badgeColor: string;
  description: string;
  visaTypes: VisaType[];
  documents: string[];
  steps: { title: string; desc: string }[];
  faqs: { q: string; a: string }[];
}

export const countries: Record<string, CountryData> = {
  usa: {
    slug: "usa",
    flag: "🇺🇸",
    name: "АНУ",
    nameEn: "United States of America",
    tagline: "Америкийн мөрөөдлийг биелүүл",
    heroColor: "from-blue-900 via-blue-800 to-red-900",
    accentColor: "#1d4ed8",
    badgeColor: "bg-blue-50 text-blue-700 border-blue-200",
    description:
      "АНУ-ын виз авах нь нарийн процесс шаарддаг боловч манай мэргэжлийн зөвлөхүүд таны өргөдлийг амжилттай болгоход бүрэн дэмжлэг үзүүлнэ. 10 жилийн олон удаагийн виз авснаар та АНУ-д чөлөөтэй зорчих боломжтой болно.",
    visaTypes: [
      {
        name: "Жуулчны виз (B1/B2)",
        duration: "10 жил (олон удаагийн)",
        price: "₮850,000",
        description: "Аялал жуулчлал, бизнес уулзалт, гэр бүлийн айлчлалд зориулсан виз.",
        processingTime: "3–6 долоо хоног",
      },
      {
        name: "Оюутны виз (F-1)",
        duration: "Сургалтын хугацаанд",
        price: "$1,300 (хямдралтай)",
        description: "АНУ-ын их, дээд сургуульд суралцахад зориулсан виз.",
        processingTime: "4–8 долоо хоног",
      },
    ],
    documents: [
      "Хүчинтэй гадаад паспорт (6+ сарын хугацаатай)",
      "DS-160 маягт (онлайнаар бөглөсөн)",
      "Визийн хураамж төлсөн баримт",
      "Цээж зураг (5×5 см, цагаан дэвсгэртэй)",
      "Ажил эрхлэлт / орлогын баталгаа",
      "Банкны дансны хуулга (сүүлийн 3 сар)",
      "Монголын иргэний үнэмлэх хуулбар",
      "Буцах тийзний захиалга (шаардлагатай бол)",
    ],
    steps: [
      { title: "DS-160 маягт бөглөх", desc: "Онлайнаар DS-160 маягтыг нарийн бөглөж, хэвлэнэ." },
      { title: "Визийн хураамж төлөх", desc: "МВА банкаар $185 визийн хураамжийг төлнө." },
      { title: "Ярилцлагын цаг авах", desc: "АНУ-ын элчин сайдын яаманд ярилцлагын цаг захиална." },
      { title: "Ярилцлагад орох", desc: "Бэлдсэн баримт бичгүүдтэйгээ ярилцлагад оролцоно." },
      { title: "Виз хүлээн авах", desc: "Амжилттай болсон тохиолдолд 3–5 хоногийн дотор паспортоо авна." },
    ],
    faqs: [
      {
        q: "АНУ-ын визийн ярилцлага хэр хэцүү вэ?",
        a: "Зөв бэлдсэн тохиолдолд ярилцлага ихэвчлэн 5–10 минут үргэлждэг. Манай зөвлөхүүд таныг бүрэн бэлдэнэ.",
      },
      {
        q: "Визийн хүсэлт татгалзагдвал яах вэ?",
        a: "Татгалзсан шалтгааныг тодорхойлж, дутуу баримт бичгийг нэмж, дахин хүсэлт гаргах боломжтой.",
      },
      {
        q: "10 жилийн виз авах боломж хэр өндөр вэ?",
        a: "Манай үйлчлүүлэгчдийн 95% нь амжилттай виз авдаг. Зөв бэлдэлт бол гол хүчин зүйл.",
      },
    ],
  },

  australia: {
    slug: "australia",
    flag: "🇦🇺",
    name: "Австрали",
    nameEn: "Australia",
    tagline: "Австралийн боломжийг ашигла",
    heroColor: "from-yellow-800 via-yellow-700 to-green-800",
    accentColor: "#d97706",
    badgeColor: "bg-yellow-50 text-yellow-700 border-yellow-200",
    description:
      "Австрали нь дэлхийн хамгийн сайн боловсролын систем, амьдралын өндөр чанартай улсуудын нэг. Оюутны болон ажлын виз авснаар та Австралийн өргөн боломжийг ашиглах эрхтэй болно.",
    visaTypes: [
      {
        name: "Аялалын виз (Subclass 600)",
        duration: "3 сар хүртэл",
        price: "₮850,000",
        description: "Аялал, амралт, гэр бүлийн айлчлалд зориулсан виз.",
        processingTime: "4–6 долоо хоног",
      },
      {
        name: "Оюутны виз (Subclass 500)",
        duration: "Сургалтын хугацаанд",
        price: "₮500,000",
        description: "Австралийн бүртгэлтэй сургуульд суралцахад зориулсан виз.",
        processingTime: "4–6 долоо хоног",
      },
    ],
    documents: [
      "Хүчинтэй гадаад паспорт",
      "Immi account бүртгэл",
      "Элсэлтийн баталгаа (CoE) — оюутны визийн хувьд",
      "Санхүүгийн чадварлаг байдлын нотлох баримт",
      "Эрүүл мэндийн даатгал (OSHC)",
      "Эрүүл мэндийн үзлэгийн дүн",
      "Цагдаагийн тодорхойлолт",
      "Англи хэлний шалгалтын дүн (IELTS/PTE)",
    ],
    steps: [
      { title: "Сургууль / ажил олгогч сонгох", desc: "Тохиромжтой сургууль эсвэл ажил олгогчтой гэрээ байгуулна." },
      { title: "Immi account үүсгэх", desc: "Австралийн цагаачлалын системд бүртгэлтэй болно." },
      { title: "Онлайн өргөдөл гаргах", desc: "Бүх баримт бичгийг онлайнаар оруулж өргөдөл гаргана." },
      { title: "Эрүүл мэндийн үзлэг", desc: "Австралийн зөвшөөрөгдсөн эмнэлгээр үзлэг хийлгэнэ." },
      { title: "Виз хүлээн авах", desc: "Имэйлээр визийн баталгааг хүлээн авна." },
    ],
    faqs: [
      {
        q: "Австралийн оюутны виз авахад хэр их мөнгө шаардлагатай вэ?",
        a: "Жилд дор хаяж AUD 21,041 (≈₮70 сая) байгаа нотлох баримт шаардлагатай.",
      },
      {
        q: "Оюутны визтэй ажиллах боломжтой юу?",
        a: "Тийм, оюутны визтэй долоо хоногт 48 цаг хүртэл ажиллах эрхтэй.",
      },
      {
        q: "IELTS шалгалт заавал шаардлагатай юу?",
        a: "Ихэнх сургуульд IELTS 5.5–6.5 оноо шаардлагатай. Манай зөвлөхүүд бэлдэхэд туслана.",
      },
    ],
  },

  canada: {
    slug: "canada",
    flag: "🇨🇦",
    name: "Канад",
    nameEn: "Canada",
    tagline: "Канадын ирээдүйг бүтээ",
    heroColor: "from-red-900 via-red-800 to-slate-900",
    accentColor: "#dc2626",
    badgeColor: "bg-red-50 text-red-700 border-red-200",
    description:
      "Канад нь цагаачлалд хамгийн нээлттэй орнуудын нэг бөгөөд Express Entry системээр байнгын оршин суух эрх авах боломж өндөр байдаг. Манай зөвлөхүүд таны CRS оноог нэмэгдүүлэх стратеги боловсруулна.",
    visaTypes: [
      {
        name: "Жуулчны виз (TRV)",
        duration: "10 жил хүртэл",
        price: "₮850,000",
        description: "Аялал, гэр бүлийн айлчлал, бизнес уулзалтад зориулсан виз. 10 жилийн олон удаагийн виз.",
        processingTime: "2–4 долоо хоног",
      },
    ],
    documents: [
      "Хүчинтэй гадаад паспорт",
      "Биометрийн мэдээлэл (хурууны хээ, зураг)",
      "Санхүүгийн баталгаа (банкны хуулга)",
      "Ажил эрхлэлтийн баталгаа / CV",
      "Боловсролын дипломын хуулбар",
      "Цагдаагийн тодорхойлолт",
      "Эрүүл мэндийн үзлэгийн дүн",
      "Буцаж ирэх нотлох баримт (жуулчны визийн хувьд)",
    ],
    steps: [
      { title: "Профайл үүсгэх", desc: "IRCC системд Express Entry профайл үүсгэж CRS оноогоо тооцно." },
      { title: "Баримт бичиг бэлдэх", desc: "Боловсрол, ажлын туршлагын баримт бичгийг бэлдэнэ." },
      { title: "Урилга хүлээх (ITA)", desc: "Оноогоор нь урилга авах хүртэл хүлээнэ." },
      { title: "Байнгын оршин суух хүсэлт", desc: "ITA авснаас хойш 60 хоногийн дотор бүрэн өргөдөл гаргана." },
      { title: "PR карт авах", desc: "Амжилттай болсон тохиолдолд PR карт авна." },
    ],
    faqs: [
      {
        q: "Express Entry-д хамрагдахын тулд ямар шаардлага хангах ёстой вэ?",
        a: "Дор хаяж 1 жилийн ажлын туршлага, IELTS CLB 7+, боловсролын зэрэг шаардлагатай.",
      },
      {
        q: "CRS оноог хэрхэн нэмэгдүүлэх вэ?",
        a: "Канадын ажил олгогчоор дамжуулах, провинцийн нэмэлт оноо авах, хэлний шалгалтын оноог сайжруулах зэрэг аргууд байдаг.",
      },
      {
        q: "Гэр бүлийнхэн хамт явж болох уу?",
        a: "Тийм, Express Entry-ээр байнгын оршин суух эрх авсны дараа гэр бүлийнхнийг дагуулах боломжтой.",
      },
    ],
  },

  czech: {
    slug: "czech",
    flag: "🇨🇿",
    name: "Чех Улс",
    nameEn: "Czech Republic",
    tagline: "Европын зүрхэнд ажиллаж амьдар",
    heroColor: "from-blue-900 via-indigo-800 to-slate-900",
    accentColor: "#4338ca",
    badgeColor: "bg-indigo-50 text-indigo-700 border-indigo-200",
    description:
      "Чехийн Employee Card нь 3 сараас дээш хугацаанд Чех Улсад хууль ёсоор ажиллаж, оршин суух зөвшөөрөл юм. Хүчинтэй карттай бол Шенгений бүсэд ажлын бус зорилгоор 180 хоногт нийт 90 хүртэл хоног зорчих боломжтой. Одоогийн 10 нээлттэй ажлын байрны тусгай бүртгэлийн нөхцөлийг доорх холбоосоор харна уу.",
    visaTypes: [
      {
        name: "Employee Card — ажиллах, оршин суух зөвшөөрөл",
        duration: "Дээд тал нь 2 жил · сунгалт боломжтой",
        price: "€2,465 · 2 үе шаттай",
        description: "Чех Улсад 3 сараас дээш хугацаанд ажиллах хүнд олгогдох урт хугацааны зөвшөөрөл. Одоогийн бүртгэл 10 нээлттэй орон тоотой.",
        processingTime: "Нийт бэлтгэл 6–8 сар",
      },
    ],
    documents: [
      "Хүчинтэй гадаад паспорт — эхний бэлтгэх материал",
      "Employee Card анкет, цээж зураг, биометр",
      "Ажил олгогчийн гэрээ / тодорхойлолт болон тухайн ажлын байрны баримт",
      "Байрны нотолгоо, эрүүгийн лавлагаа, шаардлагатай орчуулга",
      "Банкны баталгаа, барьцаа шаардахгүй",
    ],
    steps: [
      { title: "Ажил олгогч олох", desc: "Чехийн ажил олгогчтой гэрээ байгуулж, урилга авна." },
      { title: "Баримт бичиг бэлдэх", desc: "Ажил олгогчийн материал, анкет, орчуулга болон албан шаардлагатай баримтыг бэлтгэнэ." },
      { title: "Элчин сайдын яаманд өргөдөл гаргах", desc: "Улаанбаатар дахь Чехийн элчин сайдын яаманд өргөдөл гаргана." },
      { title: "Биометр өгөх", desc: "Элчин сайдын яаманд биометрийн мэдээлэл өгнө." },
      { title: "Зөвшөөрөл хүлээн авах", desc: "Эрх бүхий байгууллагын эерэг шийдвэр гарсан тохиолдолд даатгал, явах бэлтгэлээ хийж Employee Card-аа авна." },
    ],
    faqs: [
      {
        q: "Хэдэн жил ажиллах боломжтой вэ?",
        a: "Employee Card нь дээд тал нь 2 жилээр олгогдоно. Ажлын болон оршин суух нөхцөл үргэлжилж байвал хугацаа дуусахаас өмнө сунгалт хүсэх боломжтой.",
      },
      {
        q: "Ажлын зөвшөөрлөөр Шенгений бусад улсад явж болох уу?",
        a: "Тийм. Хүчинтэй Employee Card-тай бол Шенгений бүсэд ажлын бус зорилгоор 180 хоногт нийт 90 хүртэл хоног зорчих боломжтой.",
      },
      {
        q: "10 нээлттэй байрны бүртгэл хэзээ дуусах вэ?",
        a: "Бүртгэл 10 сарын 5 хүртэл үргэлжилнэ. Нас, хууль бус оршин суусан эсэх болон ажлын байрны урьдчилсан шалгуурыг бүртгэхээс өмнө нягтална.",
      },
    ],
  },

  ireland: {
    slug: "ireland",
    flag: "🇮🇪",
    name: "Ирланд",
    nameEn: "Ireland",
    tagline: "Ногоон арлын боломжийг нээ",
    heroColor: "from-green-900 via-emerald-800 to-slate-900",
    accentColor: "#059669",
    badgeColor: "bg-green-50 text-green-700 border-green-200",
    description:
      "Ирланд нь Европын холбооны гишүүн орон бөгөөд англи хэлний боловсрол, IT болон эрүүл мэндийн салбарт ажлын байр их байдаг. Англи хэлний сургуульд суралцаж, ажлын зөвшөөрөл авах нь хамгийн алдартай зам юм.",
    visaTypes: [
      {
        name: "Англи хэлний сургуулын виз",
        duration: "8–52 долоо хоног",
        price: "₮990,000 (хямдралтай)",
        description: "Ирландын бүртгэлтэй хэлний сургуульд суралцахад зориулсан виз.",
        processingTime: "2–4 долоо хоног",
      },
    ],
    documents: [
      "Гадаад пасспорт",
      "Сургуулийн хүлээн авах захидал (манайхаар зохицулна)",
      "Сургалтын төлбөр төлсөн баримт",
      "Банкны хуулга",
      "Цээж зураг",
    ],
    steps: [
      { title: "Сургууль / ажил олгогч сонгох", desc: "Тохиромжтой сургууль эсвэл ажил олгогчийг сонгоно." },
      { title: "Онлайн өргөдөл гаргах", desc: "AVATS системд бүртгэлтэй болж, онлайнаар өргөдөл гаргана." },
      { title: "Баримт бичиг илгээх", desc: "Бүх баримт бичгийг скан хийж, онлайн системд оруулна." },
      { title: "Визийн хураамж төлөх", desc: "€100 визийн хураамжийг онлайнаар төлнө." },
      { title: "Виз хүлээн авах", desc: "Паспортоо элчин сайдын яаманд хүргэж, виз авна." },
    ],
    faqs: [
      {
        q: "Ирландад ажиллахын тулд ямар мэргэжил шаардлагатай вэ?",
        a: "IT, эрүүл мэнд, инженерийн мэргэжилтнүүдэд Critical Skills зөвшөөрөл авах боломж хамгийн өндөр байдаг.",
      },
      {
        q: "Англи хэлний сургуульд суралцаж байхдаа ажиллах боломжтой юу?",
        a: "Тийм, долоо хоногт 20 цаг хүртэл ажиллах эрхтэй.",
      },
      {
        q: "Ирландаас Европын бусад улсад явж болох уу?",
        a: "Ирланд Шенгений бүсэд ороогүй боловч Common Travel Area-гаар Их Британид чөлөөтэй зорчих боломжтой.",
      },
    ],
  },
  schengen: {
    slug: "schengen",
    flag: "🇪🇺",
    name: "Шенгений улсууд",
    nameEn: "Schengen Area",
    tagline: "26 улсаар нэг визээр зорчих боломж",
    heroColor: "from-sky-900 via-sky-800 to-blue-900",
    accentColor: "#0369a1",
    badgeColor: "bg-sky-50 text-sky-700 border-sky-200",
    description:
      "Шенгений аяллын виз нь Австри, Бельги, Франц, Герман, Итали, Испани зэрэг 26 улсаар зорчих боломж өгдөг. Нэг визээр та Европын хамгийн алдартай улсуудыг чөлөөтэй аялах боломжтой болно.",
    visaTypes: [
      {
        name: "Аяллын виз (C)",
        duration: "90 хоног хүртэл (180 хоногт)",
        price: "₮650,000 (хямдралтай)",
        description: "26 Шенгений улсаар аялан зорчих виз. Нэг визээр та олон улсаар зорчих боломжтой.",
        processingTime: "2–4 долоо хоног",
      },
    ],
    documents: [
      "Хүчинтэй гадаад паспорт (6+ сарын хүчинтэй)",
      "Цээж зураг (3.5×4.5 см, цагаан дэвсгэртэй)",
      "Визийн хураамж төлсөн баримт",
      "Аяллын даатгалалын даатгал (30,000 евроийн хамаарлтай)",
      "Буцах тийзний захиалга (баталгаажуулсан тийз)",
      "Буудлын зочилгооны баталгаа (ажлын газрын тодорхойлох бичиг)",
      "Банкны дансны хуулга (сүүлийн 3 сар)",
      "Монголын иргэний үнэмлэх хуулбар",
      "Зочилгооны хуваарьны баталгаа (зочилгоо байгаа бол)",
    ],
    steps: [
      { title: "Улсаа сонгох", desc: "Аяллах улсаагаа тааруулах улсын элчин сайдын яаманд хандана." },
      { title: "Бичиг баримт бэлдэх", desc: "Шаардлагдах бичиг баримтуудыг цуглуулан бэлдэнэ." },
      { title: "Визийн хураамж төлөх", desc: "Тухайн улсын элчин сайдын яаманд визийн хураамжийг төлнө." },
      { title: "Биометрик өгөх", desc: "Тохирооготох цагийн хуриаанд биометрик (хурууны дараас) өгнө." },
      { title: "Виз хүлээн авах", desc: "Амжилттай болсон тохиолдолд тохирооготох цагийн хуриаанд паспорттойгоо авна." },
    ],
    faqs: [
      {
        q: "Шенгений визээр ямар улсаад зорчих боломжтой вэ?",
        a: "Франц, Герман, Итали, Испани, Нидерланд, Австри, Бельги, Швейцари, Норвеги, Дани зэрэг 26 улсаар зорчих боломжтой.",
      },
      {
        q: "Шенгений виз хэдий хүртэл хүчинтэй вэ?",
        a: "Хамгийн 180 хоногт 90 хүртэл хүртэл зорчих боломжтой.",
      },
      {
        q: "Банкны дансны хуулганд хэдэн мөнгө байх шаардлагатай вэ?",
        a: "Хамгийн дансандаа аяллын хугацаанд хүрэлжих зардлаа байх шаардлагатай. Тухайлбаар дансны хуулганд хамгийн 3000 евроийн дүйцэнгээс дээш байх нь зүйтэй.",
      },
      {
        q: "Аяллын даатгалал заавалтай байна уу?",
        a: "Тийм, аяллын даатгалал заавалтай байна. Даатгалд зорчих улсаа болон буцах тийз, зочилгооны хуваарьны баталгааг бэлдэнэ.",
      },
    ],
  },
};
export const countryList = Object.values(countries);
